/**
 * CSRF Protection Service
 * 
 * Provides comprehensive CSRF protection including:
 * - Token generation with cryptographically secure random values
 * - Token validation
 * - Token storage and retrieval
 * - Integration with API requests
 * 
 * This service enhances security by protecting against CSRF attacks
 * while maintaining compatibility with existing code.
 */

// CSRF token configuration
export interface CSRFConfig {
  cookieName: string;
  headerName: string;
  cookieOptions: {
    path: string;
    sameSite: 'strict' | 'lax' | 'none';
    secure: boolean;
    maxAge: number; // seconds
  };
  tokenLength: number;
}

// Default CSRF configuration
export const DEFAULT_CSRF_CONFIG: CSRFConfig = {
  cookieName: 'XSRF-TOKEN',
  headerName: 'X-XSRF-TOKEN',
  cookieOptions: {
    path: '/',
    sameSite: 'strict',
    secure: true,
    maxAge: 86400 // 24 hours
  },
  tokenLength: 32
};

/**
 * Generate a cryptographically secure random token
 * @param length - Length of the token in bytes
 * @returns Random token string
 */
export function generateCSRFToken(length = DEFAULT_CSRF_CONFIG.tokenLength): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Store CSRF token in a cookie
 * @param token - CSRF token to store
 * @param config - CSRF configuration
 */
export function storeCSRFToken(
  token: string,
  config: CSRFConfig = DEFAULT_CSRF_CONFIG
): void {
  const { cookieName, cookieOptions } = config;
  const { path, sameSite, secure, maxAge } = cookieOptions;
  
  const cookieValue = encodeURIComponent(token);
  const expires = new Date(Date.now() + maxAge * 1000).toUTCString();
  
  document.cookie = `${cookieName}=${cookieValue}; path=${path}; expires=${expires}; SameSite=${sameSite}${secure ? '; Secure' : ''}`;
}

/**
 * Retrieve CSRF token from cookie
 * @param config - CSRF configuration
 * @returns CSRF token or null if not found
 */
export function getStoredCSRFToken(
  config: CSRFConfig = DEFAULT_CSRF_CONFIG
): string | null {
  const { cookieName } = config;
  const cookies = document.cookie.split(';');
  
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === cookieName) {
      return decodeURIComponent(value);
    }
  }
  
  return null;
}

/**
 * Validate CSRF token against stored token
 * @param token - CSRF token to validate
 * @param config - CSRF configuration
 * @returns Boolean indicating if token is valid
 */
export function validateCSRFToken(
  token: string,
  config: CSRFConfig = DEFAULT_CSRF_CONFIG
): boolean {
  const storedToken = getStoredCSRFToken(config);
  
  if (!storedToken || !token) {
    return false;
  }
  
  // Use constant-time comparison to prevent timing attacks
  return timingSafeEqual(token, storedToken);
}

/**
 * Constant-time string comparison to prevent timing attacks
 * @param a - First string
 * @param b - Second string
 * @returns Boolean indicating if strings are equal
 */
function timingSafeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) {
    return false;
  }
  
  let result = 0;
  for (let i = 0; i < a.length; i++) {
    result |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  
  return result === 0;
}

/**
 * Initialize CSRF protection by generating and storing a token
 * @param config - CSRF configuration
 * @returns Generated CSRF token
 */
export function initializeCSRFProtection(
  config: CSRFConfig = DEFAULT_CSRF_CONFIG
): string {
  const token = generateCSRFToken(config.tokenLength);
  storeCSRFToken(token, config);
  return token;
}

/**
 * Add CSRF token to request headers
 * @param headers - Request headers object
 * @param config - CSRF configuration
 * @returns Updated headers object with CSRF token
 */
export function addCSRFTokenToHeaders(
  headers: Record<string, string> = {},
  config: CSRFConfig = DEFAULT_CSRF_CONFIG
): Record<string, string> {
  const token = getStoredCSRFToken(config);
  
  if (token) {
    return {
      ...headers,
      [config.headerName]: token
    };
  }
  
  return headers;
}

/**
 * CSRF Protection Service
 */
export const csrfProtectionService = {
  generateCSRFToken,
  storeCSRFToken,
  getStoredCSRFToken,
  validateCSRFToken,
  initializeCSRFProtection,
  addCSRFTokenToHeaders
};

export default csrfProtectionService;
